export const analyticsAppSurfaces = [
  "marketing",
  "studio",
  "mobile_reader",
] as const;

export type AnalyticsAppSurface = (typeof analyticsAppSurfaces)[number];

export const analyticsPlatforms = ["web", "ios", "android"] as const;

export type AnalyticsPlatform = (typeof analyticsPlatforms)[number];

export const analyticsEnvironments = ["prod", "dev"] as const;

export type AnalyticsEnvironment = (typeof analyticsEnvironments)[number];

export const posthogEvents = {
  signupCompleted: "signup_completed",
  loginCompleted: "login_completed",
  onboardingStarted: "onboarding_started",
  onboardingStepViewed: "onboarding_step_viewed",
  onboardingCompleted: "onboarding_completed",
  onboardingDownloadConfirmShown: "onboarding_download_confirm_shown",
  onboardingDownloadConfirmContinued: "onboarding_download_confirm_continued",
  projectsViewed: "projects_viewed",
  storyCreated: "story_created",
  storyCanvasMeaningfulEdit: "story_canvas_meaningful_edit",
  canvasMassDeletionFlagged: "canvas_mass_deletion_flagged",
  firstGenerationCompleted: "first_generation_completed",
  characterDraftPrepared: "character_draft_prepared",
  characterDraftGenerationQueued: "character_draft_generation_queued",
  characterVersionPromoted: "character_version_promoted",
  characterSearchPerformed: "character_search_performed",
  characterAssemblyPrepared: "character_assembly_prepared",
  characterCandidateBatchCompleted: "character_candidate_batch_completed",
  characterUsableVerdictSubmitted: "character_usable_verdict_submitted",
  publishPreflightPassed: "publish_preflight_passed",
  publishPreflightFailed: "publish_preflight_failed",
  publishFailed: "publish_failed",
  publishStepAbandoned: "publish_step_abandoned",
  accountLinkStarted: "account_link_started",
  accountLinkCompleted: "account_link_completed",
  storyPublished: "story_published",
  publishLinkCopied: "publish_link_copied",
  publishQrShown: "publish_qr_shown",
  mobileAppOpened: "mobile_app_opened",
  mobileFeedViewed: "mobile_feed_viewed",
  mobileStoryOpened: "mobile_story_opened",
  // SP/MP classification (Phase-2). `storyOpened` fires when a feed/hero tap
  // resolves a launch flow — props carry the mode/type so we can measure
  // play-rates per mode/type and validate the classification. `mpLobbyCreated`
  // fires when an MP tap successfully spins up a lobby session.
  storyOpened: "story_opened",
  mpLobbyCreated: "mp_lobby_created",
  mobileStoryStarted: "mobile_story_started",
  mobileStoryProgressCheckpoint: "mobile_story_progress_checkpoint",
  mobileStoryCompleted: "mobile_story_completed",
  mobileStoryAbandoned: "mobile_story_abandoned",
  paywallViewed: "paywall_viewed",
  productSelected: "product_selected",
  purchaseStarted: "purchase_started",
  checkoutSessionCreated: "checkout_session_created",
  stripeRedirect: "stripe_redirect",
  purchaseCompleted: "purchase_completed",
  purchaseSuccessPageViewed: "purchase_success_page_viewed",
  purchaseAbandoned: "purchase_abandoned",
  purchaseFailed: "purchase_failed",
  paymentFailed: "payment_failed",
  trialPaymentMethodFailed: "trial_payment_method_failed",
  trialStarted: "trial_started",
  trialConverted: "trial_converted",
  subscriptionRenewed: "subscription_renewed",
  subscriptionCancelled: "subscription_cancelled",
  subscriptionRevoked: "subscription_revoked",
  subscriptionExpiredEmailSent: "subscription_expired_email_sent",
  accountSuspendedEmailSent: "account_suspended_email_sent",
  subscriptionRevokeSkippedStale: "subscription_revoke_skipped_stale",
  entitlementActive: "entitlement_active",
  paymentBlocked: "payment_blocked",
  paygPaywallViewed: "payg_paywall_viewed",
  paygToggled: "payg_toggled",
  paygPassPurchased: "payg_pass_purchased",
  paygPassExpired: "payg_pass_expired",
  paygCreditsLowWarning: "payg_credits_low_warning",
  paygCreditsBlocked: "payg_credits_blocked",
  paygTopupViewed: "payg_topup_viewed",
  mobileVideoDecodeFailed: "mobile_video_decode_failed",
  mobileNetworkError: "mobile_network_error",
  mobileScreenHang: "mobile_screen_hang",
  mobilePlaybackError: "mobile_playback_error",
  mobileDownloadFailed: "mobile_download_failed",
  mobilePurchaseError: "mobile_purchase_error",
  mobileScreenViewed: "mobile_screen_viewed",
  mobileClipPlayback: "mobile_clip_playback",
  // Front-page ranking + analytics (Phase-2). Measure engagement per curated
  // section/story so the server-driven ranking can be tuned against real
  // opens. Impression fires once per section per feed view; open fires on tap.
  frontPageSectionViewed: "front_page_section_viewed",
  frontPageStoryOpened: "front_page_story_opened",
  // Compose v2 (web, W8). Property shapes: `ComposeV2EventProperties` below;
  // the web helper is `apps/web/src/lib/analytics/compose-v2.ts`.
  composeProjectStarted: "compose_project_started",
  composeViewOpened: "compose_view_opened",
  composeChatMessageSent: "compose_chat_message_sent",
  composeFillUsed: "compose_fill_used",
  composeDepictionConfirmed: "compose_depiction_confirmed",
  composePublishAttempted: "compose_publish_attempted",
  composePublishSucceeded: "compose_publish_succeeded",
  composeMergeDecided: "compose_merge_decided",
} as const;

export type PostHogEventName =
  (typeof posthogEvents)[keyof typeof posthogEvents];

/**
 * Property shapes for the mobile error/observability events (WS5a of
 * docs/design-mocks/mobile-error-observability-plan.md). Mobile captures
 * merge these with the base surface/platform/environment defaults via
 * `buildMobileAnalyticsProperties`; `compactAnalyticsProperties` drops
 * the undefined optionals.
 */

/** Rate counter for request-shaped failures; `kind` disambiguates. */
export type MobileNetworkErrorProperties = {
  kind: "network" | "timeout" | "http" | "unexpected";
  surface?: string;
  op?: string;
  endpoint?: string;
  status?: number;
  request_id?: string;
  story_id?: string;
  fatal?: boolean;
  is_connected?: boolean;
};

/** A loading-timeout state tripped — "spinner forever" as a metric. */
export type MobileScreenHangProperties = {
  screen?: string;
  waited_ms: number;
};

/** Playback failures beyond video decode (audio/TTS/stream stalls). */
export type MobilePlaybackErrorProperties = {
  media_type: "video" | "audio" | "tts";
  phase: "load" | "mid_playback";
  recovered?: boolean;
  story_id?: string;
  surface?: string;
};

/** A story download failed after per-asset retries were exhausted. */
export type MobileDownloadFailedProperties = {
  story_id: string;
  failed_asset_kind?: string;
  bytes_done?: number;
  attempt: number;
};

/** A RevenueCat operation failed (user cancellations excluded). */
export type MobilePurchaseErrorProperties = {
  op: string;
  error_code?: string | number;
  store: "app_store" | "play_store";
};

/**
 * Compose v2 event properties. Ids only (project, conversation, node, story,
 * merge), the same ids other Studio events already carry; never prompt text,
 * names or titles.
 */
export type ComposeV2StartOption = "idea" | "script" | "footage" | "library";
export type ComposeV2View =
  | "overview"
  | "script"
  | "storyboard"
  | "map"
  | "preview"
  | "publish"
  | "frame"
  | "library";

export type ComposeV2EventProperties = {
  compose_project_started: {
    project_id: string;
    start_option: ComposeV2StartOption;
    /** The first message was typed (Describe an idea, or a prompt). */
    with_prompt: boolean;
    image_count: number;
    /** Story Library "Open in Compose" started it from a Story Work. */
    from_story_work: boolean;
  };
  compose_view_opened: {
    project_id: string;
    view: ComposeV2View;
  };
  compose_chat_message_sent: {
    project_id: string;
    conversation_id: string;
    /** This send created the thread. */
    new_thread: boolean;
  };
  compose_fill_used: {
    project_id: string;
    mode: "ask" | "browse";
    category: string;
    /** Fill box ("slot"), "replace" or "add". */
    target: string;
    /** Browse: how many items were added. */
    item_count?: number;
  };
  compose_depiction_confirmed: {
    project_id: string;
    surface: "frame_notes" | "library_item";
    count: number;
    category?: string;
  };
  compose_publish_attempted: {
    project_id: string;
    story_id?: string;
    first_publish: boolean;
    skip_checks: boolean;
  };
  compose_publish_succeeded: {
    project_id: string;
    story_id?: string;
    first_publish: boolean;
    requires_moderation: boolean;
  };
  compose_merge_decided: {
    project_id: string;
    merge_id: string;
    action: string;
  };
};

export type ComposeV2EventName = keyof ComposeV2EventProperties;

/** Navigation beacon; `screen` is a route pattern, never concrete ids. */
export type MobileScreenViewedProperties = {
  screen: string;
  previous_screen?: string;
};

/**
 * One summary per video clip played — buffering/playback health at population
 * scale (docs/design-mocks/mobile-playback-buffering-plan.md, item 0b). Gated
 * OFF by default behind the Studio-admin `mobile_config` toggle + sample rate;
 * emitted once per clip, never per frame. `buffered_ahead_at_start_ms` is the
 * key item-1 signal (bufferedPosition − currentTime at the cut); a stall at
 * `pos ≈ clip end` with 0 buffered-ahead is the end-of-clip boundary (item 2).
 */
export type MobileClipPlaybackProperties = {
  story_id?: string;
  asset_id?: string;
  session_id?: string;
  codec?: string;
  net_type?: string;
  clip_duration_ms?: number;
  readiness_latency_ms?: number;
  buffered_ahead_at_start_ms?: number;
  stall_count?: number;
  total_stall_ms?: number;
  /** True when this clip played from the file:// warm cache (a prefetch hit). */
  warm_hit?: boolean;
  /** The active prefetch preset (off/lean/balanced/aggressive/custom) for scoring. */
  prefetch_preset?: string;
  /** Cumulative bytes downloaded into the warm cache this session (waste gauge). */
  bytes_warmed?: number;
  /** True if this clip was a warmed choice branch (vs the linear next / unwarmed). */
  warm_is_choice_branch?: boolean;
  /** The author-order index of the branch that was warmed (0 = first option). */
  warm_branch_index?: number;
  surface?: string;
};

/**
 * Emitted when a feed/hero tap resolves a launch flow (SP/MP classification,
 * Phase-2). `mode`/`type` mirror the story's classification so play-rates can
 * be sliced per mode/type; `surface` is the origin (hero_carousel, for_you,
 * story_detail…). `type` is null for singleplayer.
 */
export type StoryOpenedProperties = {
  story_id: string;
  mode: "singleplayer" | "multiplayer";
  type: "pvp" | "pve_coop" | "agent_only" | null;
  surface?: string;
};

/** Emitted when an MP tap successfully creates a lobby session. */
export type MpLobbyCreatedProperties = {
  story_id: string;
  session_id?: string;
  type: "pvp" | "pve_coop" | "agent_only" | null;
  surface?: string;
};

/**
 * Front-page section impression (Phase-2 ranking analytics). Emitted once per
 * section per feed view (home/browse), so the count is a true impression, not
 * a per-scroll beacon. `sectionKind` is the DB kind ("trending"/"top_chart"/…)
 * so opens can be attributed back to how a rail was ranked. `position` is the
 * section's 0-based order within the feed.
 */
export type FrontPageSectionViewedProperties = {
  section_kind: string;
  section_id?: string;
  section_title?: string;
  surface: "home_tab" | "browse_tab";
  position: number;
  story_count: number;
};

/**
 * A story opened from a curated front-page rail (Phase-2 ranking analytics).
 * `rank` is the story's 1-based position within its section — the signal that
 * lets us measure whether the new ranking actually surfaces the stories users
 * open. Fires on tap only.
 */
export type FrontPageStoryOpenedProperties = {
  section_kind: string;
  section_id?: string;
  rank: number;
  story_id: string;
  surface: "home_tab" | "browse_tab";
};

export type AnalyticsBaseProperties = {
  app_surface: AnalyticsAppSurface;
  platform: AnalyticsPlatform;
  environment: AnalyticsEnvironment;
  creator_user_id?: string;
  entry_point?: string;
  error_message?: string;
  is_owner?: boolean;
  origin_surface?: string;
  publish_session_id?: string;
  story_id?: string;
  story_version_number?: number;
  surface_name?: string;
};

export function compactAnalyticsProperties<T extends Record<string, unknown>>(
  properties: T,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(properties).filter(([, value]) => value !== undefined),
  );
}

function fallbackRandomId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function createRandomAnalyticsId(prefix: string) {
  const randomUUID = globalThis.crypto?.randomUUID;
  const suffix =
    typeof randomUUID === "function"
      ? randomUUID.call(globalThis.crypto)
      : fallbackRandomId();
  return `${prefix}_${suffix}`;
}

export function createPublishSessionId() {
  return createRandomAnalyticsId("publish");
}

export function getAnalyticsEnvironment(value?: string | null) {
  return value === "production" || value === "prod" ? "prod" : "dev";
}

export function getPlatformForOs(os: string): AnalyticsPlatform {
  return os === "ios" ? "ios" : os === "android" ? "android" : "web";
}

export function resolvePublishSessionId(
  ...values: Array<string | null | undefined>
) {
  return values.find((value) => typeof value === "string" && value.length > 0);
}

export function appendQueryParamsToPath(
  path: string,
  params: Record<string, string | number | boolean | null | undefined>,
) {
  const url = new URL(path, "https://multic.local");

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") {
      continue;
    }
    url.searchParams.set(key, String(value));
  }

  const query = url.searchParams.toString();
  return `${url.pathname}${query ? `?${query}` : ""}`;
}

export function buildMobileStoryDeepLink(params: {
  storyId: string;
  publishSessionId?: string | null;
  scheme?: string;
}) {
  const scheme = params.scheme ?? "multic";
  const url = new URL(`${scheme}://story/${params.storyId}`);
  if (params.publishSessionId) {
    url.searchParams.set("publish_session_id", params.publishSessionId);
  }
  return url.toString();
}
